import { useEffect, useState } from "react";
import { FileDown, FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { exportReportExcel } from "@/lib/excel";
import { api } from "@/lib/api";
import { rs, rs2, pct, qty, fmtDate, todayIso, monthOf, daysInMonth, addDays, iso } from "@/lib/format";
import { exportReportPdf } from "@/lib/pdf";
import { KpiCard, PageHeader, Panel } from "@/components/Kpi";
import { PaidBySplit, PaidByDateTable, PaidByHeadTable } from "@/components/PaidBy";
import { useReport } from "@/pages/Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const PRESETS = { month: "Full month", h1: "1st – 15th", h2: "16th – month end", week: "Week (Mon–Sun)", custom: "Custom range" };

function rangeFor(preset, month, anchor) {
  const dim = daysInMonth(month);
  if (preset === "month") return [`${month}-01`, `${month}-${dim}`];
  if (preset === "h1") return [`${month}-01`, `${month}-15`];
  if (preset === "h2") return [`${month}-16`, `${month}-${dim}`];
  const d = new Date(`${anchor}T00:00:00`);
  const mon = addDays(anchor, -((d.getDay() + 6) % 7));
  return [mon, addDays(mon, 6)];
}

const Th = ({ children, r }) => <th className={`py-2 px-3 text-xs uppercase tracking-wider text-slate-500 font-semibold whitespace-nowrap ${r ? "text-right" : "text-left"}`}>{children}</th>;
const Td = ({ children, r, b, className = "" }) => <td className={`py-2 px-3 whitespace-nowrap ${r ? "text-right num" : ""} ${b ? "font-semibold" : ""} ${className}`}>{children}</td>;

const DaysTable = ({ data }) => (
  <table className="w-full text-sm" data-testid="report-days-table">
    <thead className="border-b"><tr><Th>Date</Th><Th r>Plates</Th><Th r>Day</Th><Th r>Night</Th><Th r>Gross</Th><Th r>Deduction</Th><Th r>Net</Th><Th r>Var. Exp</Th><Th r>Net − Var</Th></tr></thead>
    <tbody>{data.days.map((d) => (
      <tr key={d.date} className={`border-b border-slate-100 ${d.gross ? "" : "text-slate-400"}`}>
        <Td>{fmtDate(d.date, { weekday: "short", day: "2-digit", month: "short" })}</Td><Td r>{qty(d.plates)}</Td><Td r>{rs(d.day_sales)}</Td><Td r>{rs(d.night_sales)}</Td>
        <Td r b>{rs(d.gross)}</Td><Td r>{rs(d.deduction)}</Td><Td r>{rs(d.net)}</Td><Td r className="text-rose-700">{rs(d.variable_expense)}</Td>
        <Td r className={d.net_after_variable < 0 ? "text-rose-700" : "text-emerald-700"}>{rs(d.net_after_variable)}</Td>
      </tr>))}</tbody>
    <tfoot className="bg-amber-50 font-bold text-amber-900"><tr>
      <Td>Total</Td><Td r>{qty(data.summary.plates)}</Td><Td r>{rs(data.summary.day_sales)}</Td><Td r>{rs(data.summary.night_sales)}</Td><Td r>{rs(data.summary.gross)}</Td>
      <Td r>{rs(data.summary.deduction)}</Td><Td r>{rs(data.summary.net)}</Td><Td r>{rs(data.summary.variable_expense)}</Td><Td r>{rs(data.summary.net - data.summary.variable_expense)}</Td></tr></tfoot>
  </table>
);

const ItemsTable = ({ data }) => (
  <table className="w-full text-sm" data-testid="report-items-table">
    <thead className="border-b"><tr><Th>Category</Th><Th>Item</Th><Th r>Rate</Th><Th r>Day Qty</Th><Th r>Night Qty</Th><Th r>Total Qty</Th><Th r>Sales</Th><Th r>% Sales</Th></tr></thead>
    <tbody>{data.items.map((i) => (
      <tr key={i.id} className={`border-b border-slate-100 ${i.qty ? "" : "text-slate-400"}`}>
        <Td>{i.category}</Td><Td>{i.name}</Td><Td r>₹{i.rate}</Td><Td r>{qty(i.day_qty)}</Td><Td r>{qty(i.night_qty)}</Td><Td r b>{qty(i.qty)}</Td><Td r b>{rs(i.sales)}</Td><Td r>{pct(i.pct)}</Td>
      </tr>))}</tbody>
  </table>
);

const ExpTable = ({ data }) => (
  <table className="w-full text-sm" data-testid="report-expenses-table">
    <thead className="border-b"><tr><Th>Type</Th><Th>Head</Th><Th r>Amount</Th><Th r>% of Expense</Th></tr></thead>
    <tbody>{data.expenses.map((e) => (
      <tr key={e.id} className={`border-b border-slate-100 ${e.amount ? "" : "text-slate-400"}`}>
        <Td>{e.kind === "fixed" ? "Fixed (pro-rated)" : e.category}</Td><Td>{e.name}</Td><Td r b>{rs2(e.amount)}</Td><Td r>{pct(data.summary.total_expense ? e.amount / data.summary.total_expense : 0)}</Td>
      </tr>))}</tbody>
    <tfoot className="bg-amber-50 font-bold text-amber-900"><tr><Td>Total</Td><Td /><Td r>{rs2(data.summary.total_expense)}</Td><Td r>100%</Td></tr></tfoot>
  </table>
);

export default function Reports() {
  const [preset, setPreset] = useState("month");
  const [month, setMonth] = useState(monthOf(todayIso()));
  const [anchor, setAnchor] = useState(todayIso());
  const [custom, setCustom] = useState([`${monthOf(todayIso())}-01`, todayIso()]);
  const [busy, setBusy] = useState(false);
  const [xBusy, setXBusy] = useState(false);
  useEffect(() => {
    api.get("/entries", { params: { month } }).then((r) => { if (!r.data.length) { setMonth("2026-09"); setAnchor("2026-09-15"); } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [start, end] = preset === "custom" ? custom : rangeFor(preset, month, anchor);
  const valid = start && end && start <= end;
  const data = useReport(valid ? start : iso(new Date()), valid ? end : iso(new Date()));
  const label = preset === "custom" ? "Custom Report" : `${PRESETS[preset]} Report`;

  const exportPdf = () => { setBusy(true); try { exportReportPdf(data, label); } finally { setBusy(false); } };
  const exportXlsx = async () => {
    setXBusy(true);
    try { await exportReportExcel(data); toast.success("Excel file downloaded"); }
    catch (e) { toast.error("Could not create Excel file"); } finally { setXBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Reports" subtitle="Check totals for a month, a fortnight or a week — then export the complete sheet as PDF.">
        <Select value={preset} onValueChange={setPreset}>
          <SelectTrigger className="w-48 bg-white" data-testid="date-range-select-dropdown"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(PRESETS).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`range-option-${k}`}>{v}</SelectItem>)}</SelectContent>
        </Select>
        {["month", "h1", "h2"].includes(preset) && <Input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="w-44 bg-white" data-testid="report-month-picker" />}
        {preset === "week" && <Input type="date" value={anchor} onChange={(e) => e.target.value && setAnchor(e.target.value)} className="w-44 bg-white" data-testid="report-week-picker" />}
        {preset === "custom" && <>
          <Input type="date" value={custom[0]} onChange={(e) => setCustom([e.target.value, custom[1]])} className="w-40 bg-white" data-testid="report-start-date" />
          <Input type="date" value={custom[1]} onChange={(e) => setCustom([custom[0], e.target.value])} className="w-40 bg-white" data-testid="report-end-date" />
        </>}
        <Button onClick={exportXlsx} disabled={!data || xBusy} className="bg-emerald-700 hover:bg-emerald-800" data-testid="export-excel-report-button">
          {xBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FileSpreadsheet className="h-4 w-4 mr-1" />}Export Excel
        </Button>
        <Button onClick={exportPdf} disabled={!data || busy} className="bg-slate-900 hover:bg-slate-800" data-testid="export-pdf-report-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FileDown className="h-4 w-4 mr-1" />}Export PDF
        </Button>
      </PageHeader>
      <div className="mb-6 text-sm text-slate-600" data-testid="report-range-label">
        Showing <span className="font-semibold text-slate-900">{fmtDate(start, { day: "numeric", month: "short", year: "numeric" })}</span> to <span className="font-semibold text-slate-900">{fmtDate(end, { day: "numeric", month: "short", year: "numeric" })}</span>
        {!valid && <span className="ml-2 text-rose-600">End date must be after start date</span>}
      </div>
      {!data ? <div className="text-slate-500">Loading…</div> : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
            <KpiCard testId="report-gross" label="Gross Sales" value={rs(data.summary.gross)} tone="amber" />
            <KpiCard testId="report-deduction" label={`Deduction ${data.summary.deduction_pct}%`} value={rs(data.summary.deduction)} tone="orange" />
            <KpiCard testId="report-net" label="Net Sales" value={rs(data.summary.net)} tone="blue" />
            <KpiCard testId="report-variable" label="Variable Exp." value={rs(data.summary.variable_expense)} tone="rose" />
            <KpiCard testId="report-fixed" label="Fixed (pro-rated)" value={rs(data.summary.fixed_cost)} tone="rose" />
            <KpiCard testId="report-profit" label="Net Profit" value={rs(data.summary.profit)} sub={`Margin ${pct(data.summary.margin)}`} tone={data.summary.profit >= 0 ? "emerald" : "rose"} />
          </div>
          <Panel testId="report-tables">
            <Tabs defaultValue="days">
              <TabsList className="mb-4">
                <TabsTrigger value="days" data-testid="report-tab-days">Day-wise</TabsTrigger>
                <TabsTrigger value="items" data-testid="report-tab-items">Item-wise</TabsTrigger>
                <TabsTrigger value="exp" data-testid="report-tab-expenses">Expenses</TabsTrigger>
                <TabsTrigger value="paid" data-testid="report-tab-paid-by">Paid by</TabsTrigger>
              </TabsList>
              <div className="overflow-x-auto">
                <TabsContent value="days"><DaysTable data={data} /></TabsContent>
                <TabsContent value="items"><ItemsTable data={data} /></TabsContent>
                <TabsContent value="exp"><ExpTable data={data} /></TabsContent>
                <TabsContent value="paid">
                  <div className="space-y-6">
                    <PaidBySplit paidBy={data.paid_by} />
                    <div><div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Date-wise — who paid what</div><PaidByDateTable days={data.days} paidBy={data.paid_by} /></div>
                    <div><div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Head-wise — who paid for what</div><PaidByHeadTable expenses={data.expenses} paidBy={data.paid_by} /></div>
                  </div>
                </TabsContent>
              </div>
            </Tabs>
          </Panel>
        </div>
      )}
    </div>
  );
}
